import { Modal, useModals } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { ArrowRefresh2 } from 'blaze-ui/icons'
import { Input } from 'almanac-ui/TextInputField/Input'
import { LineSkeleton } from 'components/Skeleton/LineSkeleton'
import { useRef, useState } from 'react'
import Styles from './EditCaptionModal.module.scss'
import cx from 'classnames'
import { useGenerateCaption } from 'hooks/queries/scheduledPosts/useGenerateCaption'
import { ScheduledPostTarget } from 'types'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import twitter from 'twitter-text'

interface EditCaptionModalProps {
  caption: string
  onSave: (caption: string) => void
  maxCharacters?: number
  scheduledPostId: number
  scheduledPostTargets?: ScheduledPostTarget[]
}

export const EditCaptionModal = ({ caption, onSave, scheduledPostId, scheduledPostTargets }: EditCaptionModalProps) => {
  const [localCaption, setLocalCaption] = useState(caption)
  const { closeModal } = useModals()
  const [isGenerating, setIsGenerating] = useState(false)
  const captionRef = useRef<HTMLTextAreaElement>(null)

  const hasXTarget = scheduledPostTargets?.some((target) => target.provider === ExternalIntegrationEnum.X)
  const xCharacterLimit = 280
  const weightedLength = twitter.parseTweet(localCaption).weightedLength
  const isOverXLimit = hasXTarget && weightedLength > xCharacterLimit

  const { mutate: generateCaptions } = useGenerateCaption(
    { scheduledPostId },
    {
      onSuccess: (data) => {
        try {
          const caption = data.caption
          setLocalCaption(caption)
        } finally {
          setIsGenerating(false)
          setTimeout(() => {
            captionRef.current?.focus()
          }, 0)
        }
      },
    },
  )

  const handleSave = () => {
    onSave(localCaption)
    closeModal()
  }

  const handleRegenerate = () => {
    setLocalCaption('')
    setIsGenerating(true)
    generateCaptions({ userMessage: '' })
  }

  return (
    <Modal.Root size="sm" onClose={closeModal} onPressOutside={closeModal}>
      <Modal.Header
        title="Edit Caption"
        actions={
          <Button
            variant="ghost-tertiary"
            size="lg"
            frontIcon={ArrowRefresh2}
            onPress={handleRegenerate}
            isDisabled={isGenerating}
          >
            {isGenerating ? 'Generating...' : 'Regenerate'}
          </Button>
        }
      />
      <Modal.Content>
        <div className={Styles.content}>
          <div className={Styles.captionInput}>
            <div className={Styles.inputContainer}>
              <Input
                name="caption"
                value={localCaption}
                isTextarea
                autoFocus
                isValid={!isOverXLimit}
                rows={3}
                maxHeight={400}
                onChange={(e) => setLocalCaption(e.target.value)}
                ref={captionRef}
                className={cx({
                  [Styles.hiddenInput]: isGenerating,
                  [Styles.overLimitInput]: isOverXLimit,
                })}
              />
              {isGenerating && (
                <div className={Styles.skeletonOverlay}>
                  <LineSkeleton className={Styles.skeletonLine} width="85%" />
                  <LineSkeleton className={Styles.skeletonLine} width="75%" />
                  <LineSkeleton className={Styles.skeletonLine} width="90%" />
                </div>
              )}
            </div>
            {hasXTarget && (
              <div className={cx(Styles.maxCharacters, { [Styles.overLimit]: isOverXLimit })}>
                Character Count {weightedLength} / {xCharacterLimit}
              </div>
            )}
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton variant="tertiary" size="lg" onPress={closeModal}>
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Modal.FooterButton
            variant="primary"
            size="lg"
            onPress={handleSave}
            isDisabled={isOverXLimit}
            aria-label="Save Caption"
          >
            Save Caption
          </Modal.FooterButton>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
